import { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { showToast } from '../lib/toastStore'
import { InAppBrowserBanner } from '../components/auth/InAppBrowserBanner'

export default function SignIn() {
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState('')
  const [loading,  setLoading]  = useState(false)

  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail,         setResetEmail]         = useState('')
  const [resetSent,          setResetSent]          = useState(false)
  const [resetLoading,       setResetLoading]       = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email || !password) return
    setLoading(true)
    setError('')

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      })

      if (error) {
        setError(error.message)
        return
      }

      if (data.user) {
        const pendingToken = localStorage.getItem('pending_invite_token')
        if (pendingToken) {
          localStorage.removeItem('pending_invite_token')
          window.location.href = '/join/' + pendingToken
        } else {
          const params   = new URLSearchParams(window.location.search)
          const redirect = params.get('redirect')
          window.location.href = redirect ? decodeURIComponent(redirect) : '/home'
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async () => {
    if (!resetEmail.trim()) return
    setResetLoading(true)
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        resetEmail.trim(),
        { redirectTo: 'https://pantryioapp.com/reset-password' }
      )
      if (resetError) {
        showToast('Could not send reset email. Please try again.')
        return
      }
      setResetSent(true)
    } catch {
      showToast('Something went wrong.')
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[440px]">
        <InAppBrowserBanner />
        <div className="flex justify-center mb-5">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-[16px] bg-forest text-forest-ink font-serif italic text-[32px] font-semibold leading-none">
            P
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2 text-center">
          Sign in
        </div>
        <h1 className="font-serif text-[30px] font-normal leading-[1.05] tracking-[-0.015em] text-ink text-center">
          Welcome <em className="italic">back</em>.
        </h1>
        <p className="font-serif text-[14px] italic text-ink-muted mt-1 mb-8 leading-[1.4] text-center">
          Good to see you again.
        </p>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-medium text-ink-muted uppercase tracking-[0.12em]">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-[12px] border border-border-sand bg-cream px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-medium text-ink-muted uppercase tracking-[0.12em]">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Your password"
              className="w-full rounded-[12px] border border-border-sand bg-cream px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
            />
            <div className="flex justify-end mt-1">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-[12px] text-forest font-medium underline underline-offset-4"
              >
                Forgot password?
              </button>
            </div>
          </div>

          {error && (
            <p className="rounded-[12px] bg-critical-bg border border-critical/20 text-critical text-[13px] p-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-[14px] bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-50 transition mt-2"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="text-[13px] text-ink-muted text-center mt-6">
          Don't have an account?{' '}
          <Link to="/sign-up" className="text-forest underline underline-offset-4 font-medium">
            Sign up
          </Link>
        </p>
      </div>

      {/* ── Forgot password sheet ── */}
      {showForgotPassword && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center"
          onClick={() => setShowForgotPassword(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-[460px] bg-cream rounded-t-[24px] p-5 pb-8"
            onClick={e => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-border-avatar" />

            {!resetSent ? (
              <>
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
                  Password
                </div>
                <h2 className="font-serif text-[22px] font-normal tracking-[-0.01em] text-ink">
                  Reset your <em className="italic">password</em>.
                </h2>
                <p className="font-serif text-[13px] italic text-ink-muted mt-1 mb-5 leading-[1.4]">
                  Enter your email — we'll send a reset link.
                </p>

                <input
                  type="email"
                  placeholder="your@email.com"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  autoFocus
                  className="w-full rounded-[12px] border border-border-sand bg-paper px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition mb-4"
                />

                <button
                  onClick={handleResetPassword}
                  disabled={resetLoading || !resetEmail.trim()}
                  className="w-full rounded-[14px] bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-50 mb-2"
                >
                  {resetLoading ? 'Sending…' : 'Send reset link'}
                </button>

                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full rounded-[14px] border border-border-sand bg-paper py-3 text-[13px] font-semibold text-ink"
                >
                  Cancel
                </button>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
                  Sent
                </div>
                <h2 className="font-serif text-[22px] font-normal tracking-[-0.01em] text-ink">
                  Check your <em className="italic">email</em>.
                </h2>
                <p className="font-serif text-[13px] italic text-ink-muted mt-2 leading-[1.4]">
                  We sent a reset link to{' '}
                  <span className="not-italic text-ink font-medium">{resetEmail}</span>.
                </p>
                <p className="text-[11px] italic text-ink-muted mt-3 mb-6 font-serif">
                  If it doesn't arrive in a minute, check your spam folder.
                </p>
                <button
                  onClick={() => {
                    setShowForgotPassword(false)
                    setResetSent(false)
                    setResetEmail('')
                  }}
                  className="w-full rounded-[14px] bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em]"
                >
                  Back to sign in
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
